import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ColumnName } from '@/shared/cms/column-name';
import { Progress } from '@/shared/ui/progress';

import cls from './pattern-correlation-stats.module.css';

interface CorrelationItem {
    name: string;
    value: number;
}

interface PatternCorrelationStatsTableProps {
    data: Array<CorrelationItem>;
    titleKey: string;
    variant: 'positive' | 'negative';
}

interface CorrelationTableRowProps {
    index: number;
    item: CorrelationItem;
    variant: 'green' | 'red';
}

const CorrelationTableRow = memo(
    ({ index, item, variant }: CorrelationTableRowProps) => (
        <tr>
            <td style={{ width: '10%' }}>{index + 1}</td>
            <td
                aria-label={item.name}
                style={{ width: '60%' }}
            >
                <ColumnName slug={item.name} />
            </td>
            <td>
                <Progress
                    aria-label={`${item.name} correlation: ${item.value}%`}
                    value={item.value}
                    variant={variant}
                />
            </td>
        </tr>
    ),
);

CorrelationTableRow.displayName = 'CorrelationTableRow';

export const PatternCorrelationStatsTable = memo(
    ({ data, titleKey, variant }: PatternCorrelationStatsTableProps) => {
        const { t } = useTranslation('pattern-studio');

        const variantMap = {
            positive: 'green',
            negative: 'red',
        } as const;

        return (
            <div className={cls.tableWrapper}>
                <table className={cls.table}>
                    <thead>
                        <tr>
                            <th>{t('patternStats.rank')}</th>
                            <th colSpan={2}>{t(titleKey)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <CorrelationTableRow
                                key={item.name}
                                index={index}
                                item={item}
                                variant={variantMap[variant]}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        );
    },
);

PatternCorrelationStatsTable.displayName = 'PatternCorrelationStatsTable';
